import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typography, Button, Select } from '../../../../components';
import { icons } from '../../../../icons';
import { HeadLine3 } from '../../../../components/typography/styled';
import { projectService, billingService } from '../../../../api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  min-width: 400px;
  max-width: 500px;
  background: ${({theme}) => theme.palette[10]};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const BodyModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
`;

interface ServiceChangeProjectModalProps {
  service: any;
  onChangeProject: (projectId: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ServiceChangeProjectModal: FC<ServiceChangeProjectModalProps> = ({
  service,
  onChangeProject,
  onCancel,
  loading = false,
}) => {
  const [t] = useTranslation();
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [projectsLoading, setProjectsLoading] = useState(true);
  const excludeFilterRef = useRef<string | undefined>(undefined);

  const fetchProjects = async (search?: string) => {
    try {
      const res = await projectService.getProjectList({
        page: 1,
        pageSize: 100,
        search,
        filtering: excludeFilterRef.current,
      });
      const projectList = res.data?.objects || res.data || [];
      setProjects(projectList);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      // Check if service has an associated resource to exclude it from the list
      try {
        const assocRes = await billingService.hasAssociatedResource(service.id);
        if (assocRes.data?.has_associated_resource && assocRes.data?.resource_details?.project_id) {
          excludeFilterRef.current = `project_id__ne:${assocRes.data.resource_details.project_id}`;
        }
      } catch {
        // If check fails, just load all projects
      }
      fetchProjects();
    };

    init();
  }, [service.id]);

  const handleSearchChange = (value: string) => {
    fetchProjects(value);
  };

  const handleChangeProject = () => {
    if (!selectedProject) return;
    onChangeProject(selectedProject);
  };

  const projectOptions = projects.map((project: any) => ({
    id: project.id || project.project_id,
    title: project.name || project.project_name || project.id,
  }));

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
          {t('CHANGE_PROJECT')}
        </HeadLine3>
        <Icon onClick={onCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Typography variant="body">
          {t('SELECT_PROJECT')}
        </Typography>

        {projectsLoading ? (
          <Typography variant="body">Loading projects...</Typography>
        ) : (
          <Select
            title={t('PROJECT_NAME')}
            data={projectOptions}
            selectedValue={selectedProject}
            onChange={(selected) => setSelectedProject(typeof selected === 'object' ? selected.id : selected)}
            disabled={projectsLoading}
            hasSearch
            onSearchChange={handleSearchChange}
          />
        )}

        <ButtonsWrapper>
          <Button
            variant="stroke"
            size="display"
            title={t('CANCEL')}
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            variant="primary"
            size="display"
            title={t('CHANGE_PROJECT')}
            onClick={handleChangeProject}
            loading={loading}
            disabled={!selectedProject || projectsLoading}
          />
        </ButtonsWrapper>
      </BodyModal>
    </Container>
  );
};

export default ServiceChangeProjectModal;
